import { initializeApp } from "firebase/app"
import { getFirestore, connectFirestoreEmulator, doc, setDoc, serverTimestamp, getDoc, collection, query, where, getDocs, limit } from "firebase/firestore"
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { logFileUpload } from './securityLogging'
import {
  getAuth,
  setPersistence,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  inMemoryPersistence,
  signOut as firebaseSignOut,
} from 'firebase/auth'
import { getAnalytics, isSupported, type Analytics } from "firebase/analytics"
import { getMessaging, getToken, onMessage } from "firebase/messaging"
import type { Messaging } from "firebase/messaging"
import { Capacitor } from "@capacitor/core"

// Firebase configuration is provided via Vite environment variables.
// Define the variables in a local `.env` (never commit secrets) or use
// the provided `.env.example` as a template.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

// Initialize Firebase once per app lifecycle.
const app = initializeApp(firebaseConfig)

export const db = getFirestore(app)
export const storage = getStorage(app)
export const auth = getAuth(app)

// Connect to Firebase emulators only if explicitly enabled via environment variable
// Set VITE_USE_FIREBASE_EMULATOR=true in your .env file to use local emulator
const useFirebaseEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true'


if (useFirebaseEmulator) {
  try {
    connectFirestoreEmulator(db, '127.0.0.1', 8080)
    console.log('🔧 [Firebase] Successfully connected to Firestore emulator at 127.0.0.1:8080')
  } catch (error) {
    console.error('❌ [Firebase] Failed to connect to Firestore emulator:', error)
  }
} else {
}

// Initialize Messaging safely: some in-app browsers don't support required APIs.
export let messaging: Messaging | null = null
try {
  if (typeof window !== "undefined") {
    messaging = getMessaging(app)
  }
} catch (err) {
  console.error("Failed to initialize Firebase Messaging", err)
  messaging = null
}

let analyticsPromise: Promise<Analytics | undefined> | null = null

const hasAnalyticsConsent = (): boolean => {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem('analytics-consent') === 'true'
}

// Analytics is lazy-initialized and only when consent exists.
export const getAnalyticsInstance = async (): Promise<Analytics | undefined> => {
  if (typeof window === "undefined") {
    console.log('[Firebase Analytics] Skipped: window undefined (SSR environment)')
    return undefined
  }

  if (!hasAnalyticsConsent()) {
    return undefined
  }

  if (analyticsPromise) {
    return analyticsPromise
  }

  analyticsPromise = (async () => {
    const supported = await isSupported()
    if (!supported) {
      console.warn('[Firebase Analytics] ⚠️ Firebase Analytics not supported in this environment (may be iOS private mode, restricted IndexedDB, or Capacitor limitations)')
      return undefined
    }

    try {
      const analytics = getAnalytics(app)
      console.log('[Firebase Analytics] ✅ Successfully initialized')
      return analytics
    } catch (err) {
      console.error('[Firebase Analytics] ❌ Failed to initialize:', err)
      return undefined
    }
  })()

  return analyticsPromise
}

/**
 * Compress an image file to reduce file size
 * @param file - The image file to compress
 * @param quality - Quality level 0-1 (default 0.7)
 * @returns Promise<File> - The compressed image file
 */
export async function compressImage(file: File, quality: number = 0.7): Promise<File> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    
    reader.onload = (event) => {
      const img = new Image()
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          
          if (!ctx) {
            throw new Error('Failed to get canvas context - canvas 2D context unavailable')
          }
          
          // Set canvas dimensions
          canvas.width = img.width
          canvas.height = img.height
          
          // Draw image
          ctx.drawImage(img, 0, 0)
          
          // Convert to blob and create new file
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                throw new Error('Failed to compress image - canvas.toBlob returned null')
              }
              
              const compressedFile = new File(
                [blob],
                file.name,
                { type: 'image/jpeg', lastModified: Date.now() }
              )
              
              console.log('[CompressImage] Compression complete:', {
                original: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
                compressed: `${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`,
                ratio: `${((1 - compressedFile.size / file.size) * 100).toFixed(0)}% reduction`,
                quality,
                fileName: file.name
              })
              
              resolve(compressedFile)
            },
            'image/jpeg',
            quality
          )
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : String(err)
          console.error('[CompressImage] Error during compression:', {
            error: errorMsg,
            fileName: file.name,
            dimensions: `${img.width}x${img.height}`,
            quality
          })
          reject(new Error(`Image compression failed: ${errorMsg}`))
        }
      }
      
      img.onerror = () => {
        console.error('[CompressImage] Failed to load image:', file.name)
        reject(new Error(`Failed to load image file: ${file.name}`))
      }
      
      img.src = event.target?.result as string
    }
    
    reader.onerror = () => {
      console.error('[CompressImage] Failed to read file:', file.name)
      reject(new Error(`Failed to read image file: ${file.name}`))
    }
    
    reader.readAsDataURL(file)
  })
}

/**
 * Upload an image file to Firebase Storage
 * @param file - The image file to upload
 * @param folder - The folder path in storage (e.g., 'references', 'portfolio')
 * @returns Promise<string> - The download URL of the uploaded image
 */
export async function uploadImageToStorage(file: File, folder: string, userId?: string): Promise<string> {
  try {
    // Accept files up to 50MB, but compress if needed
    const maxSizeBeforeCompress = 10 * 1024 * 1024 // 10MB - compress if larger
    const maxSizeAfterCompress = 8 * 1024 * 1024 // 8MB - target after compression
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif']
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.heic', '.heif']
    
    let fileToUpload = file
    
    // Compress if file is larger than 10MB
    if (file.size > maxSizeBeforeCompress) {
      console.log('[uploadImageToStorage] File exceeds 10MB, compressing...', {
        original: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
        fileName: file.name
      })
      
      try {
        // First compression pass (70% quality)
        fileToUpload = await compressImage(file, 0.7)
        console.log('[uploadImageToStorage] After first compression:', {
          size: `${(fileToUpload.size / 1024 / 1024).toFixed(2)}MB`,
          reduced: fileToUpload.size < file.size
        })
        
        // If still too large, compress more aggressively
        if (fileToUpload.size > maxSizeAfterCompress) {
          console.log('[uploadImageToStorage] Still too large, compressing again with 50% quality...')
          fileToUpload = await compressImage(fileToUpload, 0.5)
          console.log('[uploadImageToStorage] After second compression:', {
            size: `${(fileToUpload.size / 1024 / 1024).toFixed(2)}MB`
          })
        }
        
        // If STILL too large, try extreme compression
        if (fileToUpload.size > maxSizeAfterCompress) {
          console.log('[uploadImageToStorage] Still too large, trying extreme compression with 30% quality...')
          fileToUpload = await compressImage(fileToUpload, 0.3)
          console.log('[uploadImageToStorage] After third compression:', {
            size: `${(fileToUpload.size / 1024 / 1024).toFixed(2)}MB`
          })
        }
        
        // If STILL too large after extreme compression, give helpful error
        if (fileToUpload.size > maxSizeAfterCompress) {
          const error = `File too large even after aggressive compression (${(fileToUpload.size / 1024 / 1024).toFixed(1)}MB). Please select a different image or lower resolution photo.`
          console.error('[uploadImageToStorage] Compression failed - file still too large:', {
            original: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
            afterCompress: `${(fileToUpload.size / 1024 / 1024).toFixed(2)}MB`,
            limit: `${(maxSizeAfterCompress / 1024 / 1024).toFixed(2)}MB`
          })
          if (userId) await logFileUpload(file.name, file.size, file.type, folder, userId, false, error)
          throw new Error(error)
        }
      } catch (compressionError) {
        const error = `Failed to compress image: ${compressionError instanceof Error ? compressionError.message : 'Unknown error'}`
        console.error('[uploadImageToStorage] Compression error:', error)
        if (userId) await logFileUpload(file.name, file.size, file.type, folder, userId, false, error)
        throw new Error(error)
      }
    }
    
    // Final size check (shouldn't happen if compression worked)
    if (fileToUpload.size > maxSizeAfterCompress) {
      const error = `File too large. Maximum size: ${(maxSizeAfterCompress / 1024 / 1024).toFixed(1)}MB. Current: ${(fileToUpload.size / 1024 / 1024).toFixed(1)}MB`
      console.error('[uploadImageToStorage] Final size check failed:', error)
      if (userId) await logFileUpload(file.name, file.size, file.type, folder, userId, false, error)
      throw new Error(error)
    }
    
    // Check file type - allow empty type if extension is valid (iOS compatibility)
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
    const hasValidType = fileToUpload.type === '' || allowedTypes.includes(fileToUpload.type)
    const hasValidExtension = allowedExtensions.includes(fileExtension)
    
    if (!hasValidType && !hasValidExtension) {
      const error = `Invalid file type "${fileToUpload.type}". Please select an image file (JPEG, PNG, WEBP, GIF, or HEIC)`
      if (userId) await logFileUpload(file.name, file.size, fileToUpload.type, folder, userId, false, error)
      throw new Error(error)
    }
    
    if (!hasValidExtension) {
      const error = `Invalid file extension "${fileExtension}". Please use: ${allowedExtensions.join(', ')}`
      if (userId) await logFileUpload(file.name, file.size, file.type, folder, userId, false, error)
      throw new Error(error)
    }
    
    // Prevent path traversal
    if (folder.includes('../') || folder.includes('..\\')) {
      const error = 'Invalid folder path'
      if (userId) await logFileUpload(file.name, file.size, file.type, folder, userId, false, error)
      throw new Error(error)
    }
    
    // Generate unique filename with timestamp
    const timestamp = Date.now()
    const fileName = `${timestamp}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`
    
    // SECURITY FIX: For folders that require userId, include it in path
    const userIdRequiredFolders = ['chat-images', 'review-images']
    const storagePath = userIdRequiredFolders.includes(folder) && userId
      ? `${folder}/${userId}/${fileName}`  // Include userId for user-scoped folders
      : `${folder}/${fileName}`            // Keep original path for other folders
    
    // Create storage reference
    const storageRef = ref(storage, storagePath)
    
    // Upload file
    const snapshot = await uploadBytes(storageRef, fileToUpload)
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref)
    
    // Log successful upload
    if (userId) {
      await logFileUpload(file.name, file.size, file.type, folder, userId, true)
    }
    
    return downloadURL
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error)
    const errorDetails = {
      message: errorMsg,
      type: error instanceof Error ? error.name : typeof error,
      fileName: file.name,
      fileSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
      folder
    }
    console.error('[uploadImageToStorage] Error uploading image:', errorDetails)
    console.error('[uploadImageToStorage] Full error:', error)
    throw new Error(`Failed to upload ${file.name}: ${errorMsg}`)
  }
}

/**
 * Handle user data in Firestore after authentication
 * @param user - The Firebase User object
 * @param additionalData - Additional user data to store
 */
export async function handleUserData(user: any, additionalData?: { instagram?: string; phoneNumber?: string; createdAt?: boolean }) {
  if (!user) return

  try {
    const userRef = doc(db, 'users', user.uid)
    const userData = {
      email: user.email,
      lastLogin: serverTimestamp(),
      ...(additionalData?.createdAt && { createdAt: serverTimestamp() }),
      ...(additionalData?.instagram && { instagram: additionalData.instagram }),
      ...(additionalData?.phoneNumber && { phoneNumber: additionalData.phoneNumber })
    }

    await setDoc(userRef, userData, { merge: true })
    console.log('✅ Firestore document created/updated successfully for user:', user.uid)
  } catch (error) {
    console.error('❌ FAILED to create Firestore document for user:', user.uid)
    console.error('Error details:', error)
    console.error('Attempted data:', { email: user.email, additionalData })
    throw error
  }
}

/**
 * Update the booking flow status for a user
 * @param userId - The user's ID
 * @param flowType - The type of booking flow ('flash' or 'custom')
 * @param step - The current step in the booking flow
 */
export async function updateBookingFlowStatus(userId: string, flowType: 'flash' | 'custom', step: string) {
  if (!userId) return

  try {
    const userRef = doc(db, 'users', userId)
    await setDoc(userRef, {
      booking_flow_started: true,
      booking_flow_step: step,
      booking_flow: flowType
    }, { merge: true })
  } catch (error) {
    console.error('Error updating booking flow status:', error)
    throw error
  }
}

/**
 * Update the theme preference for a user
 * @param userId - The user's ID
 * @param themeMode - The theme mode ('light' or 'dark')
 */
export async function updateUserThemePreference(userId: string, themeMode: 'light' | 'dark') {
  if (!userId) return

  try {
    const userRef = doc(db, 'users', userId)
    await setDoc(userRef, {
      theme: themeMode
    }, { merge: true })
    console.log('Theme preference updated successfully:', themeMode)
  } catch (error) {
    console.error('Error updating theme preference:', error)
    throw error
  }
}

/**
 * Check if running on mobile
 */
function isMobile(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

/**
 * Log notification error to user document
 * @param userId - The user's ID
 * @param error - The error object or string
 * @param step - The step where the error occurred
 */
export async function logNotificationError(userId: string, error: any, step: string) {
  if (!userId) return

  try {
    const userRef = doc(db, 'users', userId)
    const errorData = {
      notificationError: {
        timestamp: serverTimestamp(),
        step: step,
        error: error instanceof Error ? {
          name: error.name,
          message: error.message,
          stack: error.stack
        } : String(error),
        userAgent: navigator.userAgent,
        url: window.location.href,
        permission: Notification.permission,
        serviceWorker: 'serviceWorker' in navigator,
        pushManager: 'PushManager' in window,
        mobile: isMobile()
      }
    }
    
    await setDoc(userRef, errorData, { merge: true })
    console.log('Notification error logged to user document:', errorData)
  } catch (logError) {
    console.error('Error logging notification error:', logError)
  }
}

/**
 * Request notification permission and get FCM token with error logging
 * @returns Promise<string | null> - The FCM token or null if permission denied
 */
export async function requestNotificationPermission(userId?: string): Promise<string | null> {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    const error = 'Notifications not supported in this environment'
    console.warn(error)
    if (userId) await logNotificationError(userId, error, 'environment_check')
    return null
  }

  const mobile = isMobile()
  console.log('Device type:', mobile ? 'Mobile' : 'Desktop')

  try {
    // Check if permission is already granted
    if (Notification.permission === 'granted') {
      console.log('Permission already granted, getting FCM token')
      return await getFCMToken(userId)
    }

    console.log('Requesting notification permission...')
    // Request permission
    const permission = await Notification.requestPermission()
    console.log('Permission result:', permission)
    
    if (permission === 'granted') {
      console.log('Permission granted, getting FCM token')
      // On mobile, add a small delay to ensure the permission is fully processed
      if (mobile) {
        console.log('Mobile device detected, adding delay...')
        await new Promise(resolve => setTimeout(resolve, 1000))
      }
      return await getFCMToken(userId)
    } else {
      const error = `Notification permission denied: ${permission}`
      console.log(error)
      if (userId) await logNotificationError(userId, error, 'permission_denied')
      return null
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error)
    if (userId) await logNotificationError(userId, error, 'permission_request')
    return null
  }
}

// Firebase Messaging service worker registration removed (native notifications pipeline in use)

/**
 * Get FCM token for the current user with error logging
 * @returns Promise<string | null> - The FCM token or null if unavailable
 */
export async function getFCMToken(userId?: string): Promise<string | null> {
  try {
    if (!messaging) {
      const error = 'Messaging not initialized'
      console.warn(error)
      if (userId) await logNotificationError(userId, error, 'messaging_init')
      return null
    }

    console.log('Getting FCM token...')
    console.log('VAPID key exists:', !!import.meta.env.VITE_FIREBASE_VAPID_KEY)
    
    const token = await getToken(messaging, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY
    })

    if (token) {
      console.log('FCM Token generated successfully:', token.substring(0, 20) + '...')
      return token
    } else {
      const error = 'No registration token available - this might be a mobile browser issue'
      console.log(error)
      if (userId) await logNotificationError(userId, error, 'token_generation')
      return null
    }
  } catch (error) {
    console.error('Error getting FCM token:', error)
    if (userId) await logNotificationError(userId, error, 'token_request')
    return null
  }
}

/**
 * Save FCM token to user document with error logging
 * @param userId - The user's ID
 * @param token - The FCM token to save
 */
export async function saveFCMToken(userId: string, token: string) {
  if (!userId || !token) {
    const error = `Invalid parameters: userId=${!!userId}, token=${!!token}`
    console.error(error)
    await logNotificationError(userId, error, 'save_token_validation')
    return
  }

  try {
    const userRef = doc(db, 'users', userId)
    const isAndroidNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'
    const notificationSettings = isAndroidNative
      ? { android: { fcmToken: token } }
      : { ios: { fcmToken: token } }

    await setDoc(userRef, {
      notificationSettings,
      notificationPermissionStatus: 'granted',
      lastTokenUpdate: serverTimestamp(),
      // Clear any previous errors when successfully saving
      notificationError: null
    }, { merge: true })
    console.log('FCM token saved successfully')
  } catch (error) {
    console.error('Error saving FCM token:', error)
    await logNotificationError(userId, error, 'save_token')
    throw error
  }
}

/**
 * Update notification permission status in user document
 * @param userId - The user's ID
 * @param status - The permission status: 'not_prompted', 'prompted', 'granted'
 */
export async function updateNotificationPermissionStatus(
  userId: string, 
  status: "granted" | "not_prompted" | "prompted" | "denied"
) {
  if (!userId) {
    console.error('No userId provided for notification permission status update')
    return
  }

  try {
    const userRef = doc(db, 'users', userId)
    await setDoc(userRef, {
      notificationPermissionStatus: status,
      lastNotificationPrompt: serverTimestamp()
    }, { merge: true })
    console.log(`Notification permission status updated to: ${status}`)
  } catch (error) {
    console.error('Error updating notification permission status:', error)
    await logNotificationError(userId, error, 'update_permission_status')
  }
}

/**
 * Get notification permission status from user document
 * @param userId - The user's ID
 * @returns Promise<string | null> - The permission status or null if not found
 */
export async function getNotificationPermissionStatus(userId: string): Promise<string | null> {
  if (!userId) return null

  try {
    const userRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userRef)
    
    if (userDoc.exists()) {
      const userData = userDoc.data()
      return userData.notificationPermissionStatus || null
    }
    
    return null
  } catch (error) {
    console.error('Error getting notification permission status:', error)
    return null
  }
}

/**
 * Check if this is the first time PWA is launched on this device
 * @returns Promise<boolean> - True if first time, false if launched before
 */
// (PWA first-launch detection removed)

/**
 * Mark PWA as launched for the first time
 */
// (PWA first-launch marker removed)

/**
 * Update PWA installation status with first launch tracking
 */
// (PWA installation status tracking removed)

/**
 * Get PWA installation status from user document
 * @param userId - The user's ID
 * @returns Promise<string | null> - The installation status or null if not found
 */
// (PWA installation status getter removed)

/**
 * Check if app is running in standalone mode (installed as PWA)
 * @returns boolean - True if app is running in standalone mode
 */
// (Standalone mode detection removed)

/**
 * Set up notification message handler
 * @param callback - Function to call when a notification is received
 */
export function setupNotificationHandler(callback: (payload: any) => void) {
  if (!messaging) {
    console.warn('Messaging not initialized')
    return
  }

  onMessage(messaging, (payload) => {
    console.log('Message received:', payload)
    callback(payload)
  })
}

/**
 * Check if user has FCM token in their document
 * @param userId - The user's ID
 * @returns Promise<boolean> - True if user has FCM token, false otherwise
 */
export async function hasFCMToken(userId: string): Promise<boolean> {
  if (!userId) return false

  try {
    const userRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userRef)
    
    if (userDoc.exists()) {
      const userData = userDoc.data()
      console.log('User data:', userData)
      const hasToken = !!(userData.fcmToken && userData.notificationPermissionStatus === 'granted')
      console.log('Has FCM token:', hasToken, 'fcmToken:', userData.fcmToken, 'notificationPermissionStatus:', userData.notificationPermissionStatus)
      return hasToken
    }
    
    console.log('User document does not exist')
    return false
  } catch (error) {
    console.error('Error checking FCM token:', error)
    return false
  }
} 

export async function initAuthPersistence() {
  try {
    console.log('[Firebase Auth] Attempting to set indexedDB persistence...')
    await setPersistence(auth, indexedDBLocalPersistence)
    console.log('[Firebase Auth] Successfully set indexedDB persistence')
  } catch (error) {
    console.warn('[Firebase Auth] indexedDB persistence failed, trying browserLocal...', error)
    try {
      await setPersistence(auth, browserLocalPersistence)
      console.log('[Firebase Auth] Successfully set browserLocal persistence')
    } catch (error2) {
      console.warn('[Firebase Auth] browserLocal persistence failed, using inMemory...', error2)
      try {
        await setPersistence(auth, inMemoryPersistence)
        console.log('[Firebase Auth] Successfully set inMemory persistence')
      } catch (error3) {
        console.error('[Firebase Auth] All persistence methods failed:', error3)
      }
    }
  } 
}

/**
 * Check if a user account exists for the given email address by querying Firestore users collection
 * @param email - Email address to check
 * @returns Promise<boolean> - true if user exists, false otherwise
 */
export async function checkUserExistsByEmail(email: string): Promise<boolean> {
  try {
    console.log('🔍 Checking if user exists in Firestore for email:', email)
    
    // Query the users collection for a document with this email
    // Add limit(1) to comply with security rules
    const usersRef = collection(db, 'users')
    const q = query(usersRef, where('email', '==', email), limit(1))
    const querySnapshot = await getDocs(q)
    
    const exists = !querySnapshot.empty
    console.log('📧 Firestore query result:', { 
      email, 
      exists, 
      docCount: querySnapshot.size,
      docs: querySnapshot.docs.map(doc => ({ id: doc.id, email: doc.data().email }))
    })
    
    return exists
  } catch (error) {
    console.error('❌ Error checking if user exists by email in Firestore:', error)
    return false
  }
}

/**
 * Centralized sign-out function with proper iOS cleanup
 * Use this instead of calling signOut(auth) directly
 * @throws Error if sign-out fails
 */
export async function signOutUser(): Promise<void> {
  try {
    console.log('[Firebase Auth] Starting sign-out process...')
    
    // Clear all cached user data to ensure proper reset of UI components like BottomNav
    localStorage.removeItem('cachedUserRole')
    localStorage.removeItem('cachedUserProfile')
    localStorage.removeItem('cachedBookingSystemStatus')
    console.log('[Firebase Auth] Cleared cached user role, profile, and booking system status')
    
    // Check if we're on iOS native
    const isIOSNative = typeof window !== 'undefined' && 
      'Capacitor' in window && 
      // @ts-ignore
      window.Capacitor?.isNativePlatform?.() && 
      // @ts-ignore
      window.Capacitor?.getPlatform?.() === 'ios'
    
    // Clean up iOS-specific resources first
    if (isIOSNative) {
      console.log('[Firebase Auth] Detected iOS native, cleaning up iOS resources...')
      try {
        // Dynamically import to avoid issues on non-iOS platforms
        const { cleanupIosOnSignOut } = await import('@/lib/notifications')
        await cleanupIosOnSignOut()
        console.log('[Firebase Auth] iOS cleanup completed')
      } catch (cleanupError) {
        console.error('[Firebase Auth] iOS cleanup failed (non-critical):', cleanupError)
        // Continue with sign-out even if cleanup fails
      }
    }
    
    // Sign out from Firebase
    console.log('[Firebase Auth] Signing out from Firebase Auth...')
    await firebaseSignOut(auth)
    console.log('[Firebase Auth] Sign-out successful')
    
  } catch (error) {
    console.error('[Firebase Auth] Sign-out error:', error)
    const errorDetails = {
      code: (error as any)?.code,
      message: (error as any)?.message,
      name: (error as any)?.name
    }
    console.error('[Firebase Auth] Error details:', errorDetails)
    throw error
  }
} 
